import { DestroyRef, Injector, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

export function setupFormDirtyTracking(
  form: AbstractControl,
  destroyRef: DestroyRef,
  injector: Injector,
  callback?: () => void,
) {
  runInInjectionContext(injector, () => {
    form.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
      form.markAsDirty();
      callback?.();
    });
  });

  if (form instanceof FormArray) {
    // Recursively setup tracking for each control in the array
    form.controls.forEach((control) => {
      setupFormDirtyTracking(control, destroyRef, injector, callback);
    });
  } else if (form instanceof FormGroup) {
    // Recursively setup tracking for each control in the group
    Object.values(form.controls).forEach((control) => {
      setupFormDirtyTracking(control, destroyRef, injector, callback);
    });
  }
}

interface DirtyStateMap {
  isDirty: boolean;
  childrenDirty: boolean;
  children?: { [key: string]: DirtyStateMap } | DirtyStateMap[];
}

/**
 * Creates a map of dirty states for all controls in a form group/array, maintaining the same structure
 * @param control The form control/group/array to analyze
 * @returns An object with the same structure as the form, where each value is an object containing:
 *          - isDirty: boolean indicating if the control itself is dirty
 *          - childrenDirty: boolean indicating if any child controls are dirty
 *          - children: the dirty state map of child controls (for FormGroup/FormArray)
 */
export function getDirtyStateMap(control: AbstractControl): DirtyStateMap {
  if (control instanceof FormGroup) {
    const dirtyMap: { [key: string]: DirtyStateMap } = {};
    let hasDirtyChildren = false;

    Object.keys(control.controls).forEach((key) => {
      const childControl = control.get(key);
      if (childControl) {
        const childState = getDirtyStateMap(childControl);
        dirtyMap[key] = childState;
        hasDirtyChildren = hasDirtyChildren || childState.isDirty || childState.childrenDirty;
      }
    });

    return {
      isDirty: control.dirty,
      childrenDirty: hasDirtyChildren,
      children: dirtyMap,
    };
  }

  if (control instanceof FormArray) {
    const childrenStates = control.controls.map((c) => getDirtyStateMap(c));
    const hasDirtyChildren = childrenStates.some((state) => state.isDirty || state.childrenDirty);

    return {
      isDirty: control.dirty,
      childrenDirty: hasDirtyChildren,
      children: childrenStates,
    };
  }

  // For FormControl, return both flags (they'll be the same for leaf nodes)
  return {
    isDirty: control.dirty,
    childrenDirty: control.dirty,
  };
}

export class FormArrayWithDirtyTracking<T extends AbstractControl> extends FormArray<T> {
  override push(value: T, options?: { emitEvent?: boolean }): void {
    super.push(value, options);
    if (options?.emitEvent !== false) {
      this.markAsDirty();
    }
  }

  override insert(index: number, value: T, options?: { emitEvent?: boolean }): void {
    super.insert(index, value, options);
    if (options?.emitEvent !== false) {
      this.markAsDirty();
    }
  }

  override removeAt(index: number, options?: { emitEvent?: boolean }): void {
    super.removeAt(index, options);
    if (options?.emitEvent !== false) {
      this.markAsDirty();
    }
  }
}
